import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { format } from 'date-fns';

const ScheduleInterviewModal = ({ isOpen, onClose, candidate, selectedDate, onSchedule }) => {
  const [formData, setFormData] = useState({
    date: selectedDate ? format(selectedDate, 'yyyy-MM-dd') : '',
    time: '10:00',
    duration: '60',
    platform: 'google-meet',
    interviewType: 'technical',
    interviewers: '',
    notes: '',
    sendReminder: true,
    reminderTime: '24'
  });

  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');

  const platformOptions = [
    { value: 'google-meet', label: 'Google Meet' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'microsoft-teams', label: 'Microsoft Teams' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'in-person', label: 'In-Person' }
  ];

  const durationOptions = [
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1.5 hours' },
    { value: '120', label: '2 hours' }
  ];

  const interviewTypeOptions = [
    { value: 'technical', label: 'Technical Round' },
    { value: 'hr', label: 'HR Round' },
    { value: 'managerial', label: 'Managerial Round' },
    { value: 'cultural-fit', label: 'Cultural Fit' },
    { value: 'final', label: 'Final Round' }
  ];

  const reminderTimeOptions = [
    { value: '1', label: '1 hour before' },
    { value: '24', label: '24 hours before' },
    { value: '48', label: '48 hours before' },
    { value: '72', label: '3 days before' }
  ];

  const handleGenerateMeetingLink = () => {
    setIsGeneratingLink(true);
    setTimeout(() => {
      const links = {
        'google-meet': `https://meet.google.com/${Math.random()?.toString(36)?.substring(7)}`,
        'zoom': `https://zoom.us/j/${Math.floor(Math.random() * 1000000000)}`,
        'microsoft-teams': `https://teams.microsoft.com/l/meetup-join/${Math.random()?.toString(36)?.substring(7)}`
      };
      setMeetingLink(links?.[formData?.platform] || '');
      setIsGeneratingLink(false);
    }, 1000);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSchedule?.({
      ...formData,
      candidate,
      meetingLink,
      scheduledDateTime: `${formData?.date}T${formData?.time}:00`
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Schedule Interview</h2>
            {candidate && (
              <p className="text-sm text-muted-foreground mt-1">
                {candidate?.candidateName} - {candidate?.position}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              type="date"
              label="Interview Date"
              required
              value={formData?.date}
              onChange={(e) => setFormData({ ...formData, date: e?.target?.value })}
            />
            <Input
              type="time"
              label="Interview Time"
              required
              value={formData?.time}
              onChange={(e) => setFormData({ ...formData, time: e?.target?.value })}
            />
          </div>

          {/* Duration and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Duration"
              options={durationOptions}
              value={formData?.duration}
              onChange={(value) => setFormData({ ...formData, duration: value })}
            />
            <Select
              label="Interview Type"
              options={interviewTypeOptions}
              value={formData?.interviewType}
              onChange={(value) => setFormData({ ...formData, interviewType: value })}
            />
          </div>

          {/* Platform Selection */}
          <Select
            label="Meeting Platform"
            options={platformOptions}
            value={formData?.platform}
            onChange={(value) => setFormData({ ...formData, platform: value })}
          />

          {/* Meeting Link Generation */}
          {['google-meet', 'zoom', 'microsoft-teams']?.includes(formData?.platform) && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Video Meeting Link</label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="Meeting link will be generated"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e?.target?.value)}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerateMeetingLink}
                  loading={isGeneratingLink}
                  iconName="Link"
                >
                  Generate
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                A unique meeting link will be generated and sent to the candidate
              </p>
            </div>
          )}

          {/* Interviewers */}
          <Input
            label="Interviewers"
            placeholder="Enter interviewer names (comma separated)"
            value={formData?.interviewers}
            onChange={(e) => setFormData({ ...formData, interviewers: e?.target?.value })}
            description="Add names of people who will conduct the interview"
          />

          {/* Notes */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Additional Notes</label>
            <textarea
              rows={3}
              placeholder="Add any special instructions or notes for the interview"
              value={formData?.notes}
              onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {/* Reminder Settings */}
          <div className="space-y-3 p-4 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendReminder"
                checked={formData?.sendReminder}
                onChange={(e) => setFormData({ ...formData, sendReminder: e?.target?.checked })}
                className="h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-ring"
              />
              <label htmlFor="sendReminder" className="text-sm font-medium text-foreground">
                Send automated reminders
              </label>
            </div>
            {formData?.sendReminder && (
              <Select
                label="Reminder Time"
                options={reminderTimeOptions}
                value={formData?.reminderTime}
                onChange={(value) => setFormData({ ...formData, reminderTime: value })}
              />
            )}
            <p className="text-xs text-muted-foreground">
              Reminders will be sent via email and SMS to both candidate and interviewers
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" iconName="Calendar">
              Schedule Interview
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleInterviewModal;